import * as aq from 'arquero';
import { applyTransform } from '../../../core/transforms';
import { SchemaEngine, ColumnType } from '../../../core/schema-engine';
import { DialogStore } from '../../stores/DialogStore';
import { AppStore } from '../../stores/AppStore';
import { StepService } from '../../services/StepService';
import type { PreviewResult } from '../preview-engine';
import i18n from '../../../i18n';
import type { SplitMode } from '../../../types/modes';

export function detectDelimiter(column: string) {
  const data = AppStore.currentData.value;
  if (!column || !data || data.length === 0) return null;
  const delimiters = [
    { char: ',', name: 'Comma', isRegex: false },
    { char: ';', name: 'Semicolon', isRegex: false },
    { char: '|', name: 'Pipe', isRegex: false },
    { char: '/', name: 'Forward Slash', isRegex: false },
    { char: '-', name: 'Hyphen', isRegex: false },
    { char: '@', name: '@ Sign', isRegex: false },
    { char: '\t', name: 'Tab', isRegex: false },
    { char: '\\s+', name: 'Whitespace', isRegex: true },
    { char: '\\', name: 'Backslash', isRegex: false },
  ];
  const sampleSize = Math.min(100, data.length);
  const sample = data.slice(0, sampleSize);
  const counts = delimiters.map((delim) => {
    let totalOccurrences = 0;
    let rowsWithDelimiter = 0;
    sample.forEach((row) => {
      const value = row[column];
      if (value != null) {
        const str = String(value);
        let matches;
        if (delim.isRegex) matches = str.match(new RegExp(delim.char, 'g'));
        else
          matches = str.match(new RegExp(delim.char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'));
        if (matches && matches.length > 0) {
          totalOccurrences += matches.length;
          rowsWithDelimiter++;
        }
      }
    });
    const consistency = sampleSize > 0 ? rowsWithDelimiter / sampleSize : 0;
    const score = consistency * (totalOccurrences / Math.max(sampleSize, 1));
    return { ...delim, count: totalOccurrences, rowsWithDelimiter, consistency, score };
  });
  const threshold = Math.max(2, sampleSize * 0.05);
  const validDelimiters = counts
    .filter((d) => d.rowsWithDelimiter >= threshold)
    .sort((a, b) => {
      if (Math.abs(a.consistency - b.consistency) > 0.1) return b.consistency - a.consistency;
      return b.count - a.count;
    });
  return validDelimiters.length > 0 ? validDelimiters[0] : null;
}

/**
 * Pure preview computation — called from useTransformPreview in the component.
 */
export function applySplitPreview(
  column: string,
  delimiter: string,
  isRegex: boolean,
  mode: SplitMode,
  maxColumns: number,
  keepOriginal: boolean
): PreviewResult | null {
  const data = AppStore.currentData.value;
  const columns = AppStore.columns.value;
  const sources = AppStore.sources.value;
  const models = AppStore.models.value;

  if (!column || !delimiter) return null;

  const transform = {
    split: {
      column,
      delimiter,
      isRegex,
      mode,
      maxColumns: mode === 'firstN' || mode === 'lastN' ? maxColumns : undefined,
      keepOriginal,
    },
  };

  const samples = data!.slice(0, 50);
  const table = aq.from(samples);
  const context = { sources, models };
  const result = applyTransform(table, transform, columns, context);

  const resultColumns = result.columnNames();
  const newCols = resultColumns.filter((c: string) => c.startsWith(`${column}_`));

  const previewColumns = [column, ...newCols];
  const fullRows = result.objects();

  const previewRows = fullRows.map((row: any, idx: number) => {
    const sourceRow = samples[idx];
    const previewRow: any = {};

    if (!keepOriginal) {
      previewRow[column] = sourceRow[column];
      previewRow._removedColumns = [column];
    } else {
      previewRow[column] = row[column];
    }

    for (const newCol of newCols) {
      previewRow[newCol] = row[newCol];
    }

    return previewRow;
  });

  return {
    title: 'Split Preview',
    stats: keepOriginal
      ? `${newCols.length} new columns created`
      : `Original column removed, ${newCols.length} new columns created`,
    columns: previewColumns,
    newColumns: newCols,
    rows: previewRows,
  };
}

export async function applySplitTransform(callbacks: any) {
  const state = DialogStore.activeDialogState.value;
  if (!state) return;

  const column = state.column as string;
  const delimiter = state.delimiter as string;
  const isRegex = state.isRegex as boolean;
  const mode = state.mode as SplitMode;
  const maxColumns = state.maxColumns as number;
  const keepOriginal = state.keepOriginal as boolean;

  const data = AppStore.currentData.value;
  const columns = AppStore.columns.value;
  const sources = AppStore.sources.value;
  const models = AppStore.models.value;

  if (!column) {
    await callbacks.onError?.(i18n.t('validation.selection.column', { ns: 'errors' }));
    return;
  }
  if (!delimiter) {
    await callbacks.onError?.(i18n.t('validation.required.delimiter', { ns: 'errors' }));
    return;
  }

  if (callbacks.onTransformStart) callbacks.onTransformStart('Splitting column...');
  try {
    const splitTransform = {
      split: {
        column,
        delimiter,
        isRegex,
        mode,
        maxColumns: mode === 'firstN' || mode === 'lastN' ? maxColumns : undefined,
        keepOriginal,
      },
    };
    let table = aq.from(data!);
    const context = { sources, models };
    let result = applyTransform(table, splitTransform, columns, context);

    const newColumns = result.columnNames().filter((name: string) => name.startsWith(`${column}_`));
    const hasTypesStep = newColumns.length > 0;

    await StepService.applyStepResult(splitTransform, result, {
      ...callbacks,
      closeDialogAfter: !hasTypesStep,
    });

    if (hasTypesStep) {
      const typeSpecs: Record<string, string> = {};
      const currentData = AppStore.currentData.value;
      for (const colName of newColumns) {
        const sampleValues = currentData!.slice(0, 100).map((row) => row[colName]);
        const inferredType = SchemaEngine.inferType(sampleValues);
        typeSpecs[colName] = inferredType;
      }
      const typesTransform = { types: typeSpecs as Record<string, ColumnType> };
      await StepService.runTransform('Auto-Type Split Columns', typesTransform, callbacks);
    }
  } catch (error: any) {
    console.error('Split transform error:', error);
    await callbacks.onError?.(
      i18n.t('transform.splitFailed', { ns: 'errors', message: error.message })
    );
  } finally {
    if (callbacks.onTransformEnd) callbacks.onTransformEnd();
  }
}
